import type { PricedPlan } from '@shared/types';

/** `productId` of the rolled-up overflow row; never a real catalog product. */
export const OTHER_CHANNELS_ROW_ID = '__other_channels__';

/**
 * Line rows the budget_summary table shows before rolling the rest into
 * "Other channels (N)". Sized to the two-column layout (BudgetSummaryPage),
 * measured at 1920x1080 on 2026-09-28: rows are 56px, and header + 8 rows +
 * total leave 88px under a one-line intro (55px under two lines, 22px under
 * three). A 9th row would clip under a two-line intro. It was 7 under the
 * old table-over-tiles stack and still overflowed there — change it only
 * with a render check of the page.
 */
export const INVESTMENT_MAX_ROWS = 8;

export interface InvestmentRow {
  productId: string;
  label: string;
  dates: string;
  monthlyBudget: number;
}

export interface InvestmentTotalRow {
  label: string;
  monthlyBudget: number;
}

export interface InvestmentTable {
  rows: InvestmentRow[];
  totalRow: InvestmentTotalRow;
  /** `subtotalMonthly × durationMonths` — the client-facing total over the flight. */
  totalOverTerm: number;
}

/**
 * Builds the `budget_summary` (brief §4 `17 investment`) table: one row per
 * `PricedLine` plus a total row, every figure read straight off the
 * already-priced `PricedPlan` so the table foots by construction.
 *
 * Fees are DELIBERATELY absent. The client-facing investment slides show
 * media only — no management fee row, no one-time setup — so the total row
 * is `subtotalMonthly` (Σ rows), not `totalMonthly`, and the term total is
 * `subtotalMonthly × durationMonths`, not `grandTotal`. Both of the latter
 * fold in fees and would leave the visible rows not adding up.
 *
 * A plan with more lines than the slide can hold does NOT simply lose the
 * tail: the overflow is rolled into one "Other channels (N)" row carrying
 * their combined budget. The lean and aggressive tiers are allocated fresh
 * across the whole catalog (engines/tiers.ts) and routinely run to 20+
 * lines, so without this the visible rows would sit well under the total
 * printed beneath them.
 *
 * Pure and framework-free so it is unit-testable without mounting React.
 */
export function buildInvestmentTable(
  plan: PricedPlan,
  labelFor: (productId: string) => string,
  flightLabel: string,
  maxRows = INVESTMENT_MAX_ROWS,
): InvestmentTable {
  const toRow = (line: PricedPlan['lines'][number]): InvestmentRow => ({
    productId: line.productId,
    label: labelFor(line.productId),
    dates: flightLabel,
    monthlyBudget: line.monthlyBudget,
  });

  let rows: InvestmentRow[];
  if (plan.lines.length <= maxRows) {
    rows = plan.lines.map(toRow);
  } else {
    const shown = plan.lines.slice(0, maxRows - 1);
    const rest = plan.lines.slice(maxRows - 1);
    rows = [
      ...shown.map(toRow),
      {
        productId: OTHER_CHANNELS_ROW_ID,
        label: `Other channels (${rest.length})`,
        dates: flightLabel,
        monthlyBudget: rest.reduce((total, line) => total + line.monthlyBudget, 0),
      },
    ];
  }

  return {
    rows,
    totalRow: { label: 'Monthly total', monthlyBudget: plan.subtotalMonthly },
    totalOverTerm: plan.subtotalMonthly * plan.durationMonths,
  };
}
